import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { ToastContainer } from "react-toastify";
import { HelmetProvider } from "react-helmet-async";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import SimpleDesignTool from "./SimpleDesignTool";
import { AuthProvider, useAuth } from "./oms/AuthContext";
import LoginPage from "./oms/LoginPage";
import OMSDashboard from "./oms/OMSDashboard";
import CustomerPortal from "./oms/CustomerPortal";

import Header from "./storefront/Header";
import Footer from "./storefront/Footer";
import HomePage from "./storefront/HomePage";
import Products from "./storefront/Products";
import ProductDetail from "./storefront/ProductDetail";
import DesignStudio from "./storefront/DesignStudio";

import FAQ from "./pages/FAQ";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Cart from "./pages/Cart";
import RequestCall from "./pages/RequestCall";
import OrderConfirmation from "./pages/OrderConfirmation";
import DesignServices from "./pages/DesignServices";
import AdminDashboard from "./pages/AdminDashboard";
import ConciergeQuote from "./pages/ConciergeQuote";
import CustomerDashboard from "./pages/CustomerDashboard";
import ThankYouPage from "./pages/ThankYouPage";
import CheckoutCancelledPage from "./pages/CheckoutCancelledPage";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ProofPage from "./pages/ProofPage";
import DesignLibrary from "./pages/DesignLibrary";
import EventPackages from "./pages/EventPackages";
import TemplateLibrary from "./pages/TemplateLibrary";

// Initialize Google Analytics
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
if (GA_ID) {
  ReactGA.initialize(GA_ID);
}

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (GA_ID) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location]);
  return null;
};

const StaffPortal = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500">Loading...</div>;
  if (!user) return <LoginPage />;
  return <OMSDashboard />;
};

function App() {
  const [cartItems, setCartItems] = useState([]);
  const [showDesignTool, setShowDesignTool] = useState(false);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartItems(savedCart);
  }, []);

  const handleSaveDesign = (imageData) => {
    console.log('Design saved:', imageData);
    setShowDesignTool(false);
  };

  return (
    <div className="App min-h-screen bg-gray-50">
      <HelmetProvider>
      <AuthProvider>
      <BrowserRouter>
        <RouteTracker />
        <Routes>
          <Route path="/staff/*" element={<StaffPortal />} />
          <Route path="/*" element={
            <>
            <Header cartItems={cartItems} />
            <main>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<Products />} />
                <Route path="/product/:productId" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/request-call" element={<RequestCall />} />
                <Route path="/concierge-quote" element={<ConciergeQuote />} />
                <Route path="/customer-dashboard" element={<CustomerDashboard />} />
                <Route path="/track" element={<CustomerPortal />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/order-confirmation" element={<OrderConfirmation />} />
                <Route path="/design-studio" element={<DesignStudio />} />
                <Route path="/design-services" element={<DesignServices />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/checkout-cancelled" element={<CheckoutCancelledPage />} />
                <Route path="/proof/:proofId" element={<ProofPage />} />
                <Route path="/my-designs" element={<DesignLibrary />} />
                <Route path="/event-packages" element={<EventPackages />} />
                <Route path="/templates" element={<TemplateLibrary />} />
              </Routes>
            </main>
        
            <Footer />
        
            {showDesignTool && (
              <SimpleDesignTool
                onSaveDesign={handleSaveDesign}
                onClose={() => setShowDesignTool(false)}
              />
            )}

            </>
          } />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
      </HelmetProvider>
      <ToastContainer position="top-right" />
    </div>
  );
}

export default App;
